import {useEffect, useMemo, useState} from "react"
import {useLocation, useSearchParams} from "react-router-dom"
import {Ban, EllipsisVertical, Trash2} from "lucide-react"
import type {RowSelectionState} from "@tanstack/react-table"
import type {OtkBlank} from "shared"

import {AnnulDialog} from "@/components/documents/AnnulDialog"
import {DataTable} from "@/components/DataTable"
import {buildWarehouseColumns} from "@/components/warehouse/columns"
import {ActViewDialog} from "@/components/warehouse/ActViewDialog"
import {DestructionActDialog} from "@/components/warehouse/DestructionActDialog"
import {Button} from "@/components/ui/button"
import {Card, CardContent} from "@/components/ui/card"
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,} from "@/components/ui/dropdown-menu"
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue,} from "@/components/ui/select"
import {Tabs, TabsList, TabsTrigger} from "@/components/ui/tabs"
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {annulActionAvailability} from "@/lib/annulAvailability"
import type {DestructionActMode} from "@/lib/destructionAct"
import {
    isAnnulledDestructionCandidate,
    isAvailableDamagedCandidate,
    isWarehouseTabStatus,
    isWarehouseTypeFilter,
    warehouseTabLabels,
    type WarehouseTabStatus,
    warehouseTabStatuses,
    type WarehouseTypeFilter,
    warehouseTypeFilterLabels,
    warehouseTypeFilters,
} from "@/lib/warehouse"
import {useReadOnly, useRolePermissions, useSession} from "@/store/session"
import {useWarehouseStore} from "@/store/warehouse"

const DEFAULT_TAB: WarehouseTabStatus = "available"
const DEFAULT_TYPE_FILTER: WarehouseTypeFilter = "all"

/**
 * warehouse.md's `/warehouse/blanks` — status Tabs, the «Показувати» type
 * filter, the per-tab `DataTable`, the bulk bar, and the two destruction-act
 * dialogs. Real data via `GET /otk/blanks` (one request per tab+type, see
 * `store/warehouse.ts`) — no `punktId` scoping param exists on that endpoint
 * yet (the real `OtkBlank` has no пункт field at all), so unlike the page
 * spec's П4 rule this shows every blank of the subject, not just the
 * account's own пункт. Stated once here rather than repeated per section.
 */
export function Warehouse() {
  const byStatus = useWarehouseStore((s) => s.byStatus)
  const fetchTab = useWarehouseStore((s) => s.fetchTab)
  const loadMore = useWarehouseStore((s) => s.loadMore)
  const annulDocument = useWarehouseStore((s) => s.annulDocument)
  const submitDestructionAct = useWarehouseStore((s) => s.submitDestructionAct)
  const applyDestructionAct = useWarehouseStore((s) => s.applyDestructionAct)
  const documentSummaries = useWarehouseStore((s) => s.documentSummaries)
  const fetchDocumentSummary = useWarehouseStore((s) => s.fetchDocumentSummary)

  const readOnly = useReadOnly()
  const { manageWarehouseDistribution, signAndSendEdrtz } = useRolePermissions()
  const userName = useSession((s) => s.user?.name ?? "")
  const subjectName = useSession((s) => s.subject?.nazva ?? "")

  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get("tab")
  const tab = isWarehouseTabStatus(tabParam) ? tabParam : DEFAULT_TAB
  const typeParam = searchParams.get("type")
  const typeFilter = isWarehouseTypeFilter(typeParam) ? typeParam : DEFAULT_TYPE_FILTER

  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [actDraft, setActDraft] = useState<{ mode: DestructionActMode; ids: string[] } | null>(
    null
  )
  const [viewedActId, setViewedActId] = useState<string | null>(null)

  useEffect(() => {
    fetchTab(tab, typeFilter)
  }, [tab, typeFilter, fetchTab])

  const tabState = byStatus[tab]

  // «Використані»'s row action needs each linked document's real status/
  // type/signedAt to run `annulActionAvailability` — fetch every distinct
  // id once per page of rows (the store dedupes/caches, see
  // `fetchDocumentSummary`).
  useEffect(() => {
    if (tab !== "used") return
    for (const blank of tabState.blanks) {
      if (blank.documentId) fetchDocumentSummary(blank.documentId)
    }
  }, [tab, tabState.blanks, fetchDocumentSummary])

  const canDestroy = !readOnly && manageWarehouseDistribution
  const tabMode: DestructionActMode | null =
    tab === "annulled" ? "annulled" : tab === "available" ? "availableDamaged" : null
  const canSelectForDestruction = canDestroy && tabMode !== null

  const eligibleIds = useMemo(() => {
    if (!tabMode) return new Set<string>()
    const predicate = tabMode === "annulled" ? isAnnulledDestructionCandidate : isAvailableDamagedCandidate
    return new Set(tabState.blanks.filter(predicate).map((b) => b.id))
  }, [tabState.blanks, tabMode])

  const selectedEligibleIds = useMemo(
    () => Object.keys(rowSelection).filter((id) => rowSelection[id] && eligibleIds.has(id)),
    [rowSelection, eligibleIds]
  )

  // Second line of defense, ported from the wireframe: re-checks each
  // blank against its *mode's own* eligibility predicate at render time,
  // not just id membership — a row that became ineligible between
  // selection and this render (status changed, tab data refreshed) is
  // dropped rather than handed to the dialog stale.
  const actDraftBlanks = actDraft
    ? tabState.blanks.filter(
        (b) =>
          actDraft.ids.includes(b.id) &&
          (actDraft.mode === "annulled"
            ? isAnnulledDestructionCandidate(b)
            : isAvailableDamagedCandidate(b))
      )
    : []
  // "Номер документа"'s `NavLink` state — the exact current URL (tab +
  // type filter both included), so closing the document modal comes back
  // to exactly where the user was, matching the wireframe's `returnTo`.
  const returnTo = `${location.pathname}${location.search}`

  function changeStatusTab(nextTab: string) {
    const next = new URLSearchParams(searchParams)
    if (nextTab === DEFAULT_TAB) next.delete("tab")
    else next.set("tab", nextTab)
    setSearchParams(next, { replace: true })
    setRowSelection({})
  }

  function changeTypeFilter(nextType: string) {
    const next = new URLSearchParams(searchParams)
    if (nextType === DEFAULT_TYPE_FILTER) next.delete("type")
    else next.set("type", nextType)
    setSearchParams(next, { replace: true })
    setRowSelection({})
  }

  async function handleFinalize(
    signedPayload: string,
    kepSignature: string
  ): Promise<{ ok: true } | { ok: false; message: string }> {
    const result = await submitDestructionAct(signedPayload, kepSignature)
    if (result.ok) {
      setActDraft(null)
      setRowSelection({})
      // The registry didn't confirm the act: show it with its retry action
      // instead of closing silently over blanks that are now claimed.
      if (result.act.status === "SUBMISSION_PENDING") setViewedActId(result.act.id)
      return { ok: true }
    }
    return { ok: false, message: result.message }
  }

  const columns = useMemo(
    () =>
      buildWarehouseColumns({
        tab,
        onViewAct: setViewedActId,
        canSelectForDestruction,
        returnTo,
      }),
    [tab, canSelectForDestruction, returnTo]
  )

  function renderRowActions(blank: OtkBlank) {
    if (tab === "used") {
      if (!blank.documentId) return null
      const summary = documentSummaries[blank.documentId]
      const availability = annulActionAvailability({
        document: summary === "loading" || summary === "error" ? undefined : summary,
        hasPermission: signAndSendEdrtz,
        readOnly,
      })
      if (availability.kind === "hidden") return null
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={`Дії з бланком ${blank.series} ${blank.number}`}
              size="icon"
              variant="ghost"
            >
              <EllipsisVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-auto">
            {availability.kind === "blocked" && (
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* Not `disabled`: Radix skips disabled items during
                      keyboard navigation, which would make the reason
                      unreachable. `aria-disabled` + a suppressed `onSelect`
                      keep it focusable while doing nothing on activation. */}
                  <DropdownMenuItem
                    aria-disabled="true"
                    className="opacity-50 focus:bg-transparent"
                    onSelect={(e) => e.preventDefault()}
                    variant="destructive"
                  >
                    <Ban data-icon="inline-start" />
                    Анулювати документ
                    <span className="sr-only">{` — недоступно: ${availability.message}`}</span>
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent>{availability.message}</TooltipContent>
              </Tooltip>
            )}
            {availability.kind === "allowed" && (
              <AnnulDialog
                documentNumber={blank.documentNumber ?? ""}
                onConfirm={(reason) => annulDocument(blank.id, reason)}
                trigger={
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} variant="destructive">
                    <Ban data-icon="inline-start" />
                    Анулювати документ
                  </DropdownMenuItem>
                }
              />
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
    if (tab === "annulled") {
      if (!canDestroy || !isAnnulledDestructionCandidate(blank)) return null
      return (
        <Button
          aria-label={`Знищити за актом бланк ${blank.series} ${blank.number}`}
          onClick={() => setActDraft({ mode: "annulled", ids: [blank.id] })}
          size="sm"
          variant="outline"
        >
          <Trash2 data-icon="inline-start" />
          <span className="max-lg:hidden">Знищити</span>
        </Button>
      )
    }
    if (tab === "available") {
      if (!canDestroy || !isAvailableDamagedCandidate(blank)) return null
      return (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              aria-label={`Дії з бланком ${blank.series} ${blank.number}`}
              size="icon"
              variant="ghost"
            >
              <EllipsisVertical />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-auto">
            <DropdownMenuItem
              onSelect={() => setActDraft({ mode: "availableDamaged", ids: [blank.id] })}
              variant="destructive"
            >
              <Trash2 data-icon="inline-start" />
              Знищити
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    }
    return null
  }

  return (
    <>
      {/* design-system.md's print rule: this page's own on-screen content is
          wrapped in its own `print:hidden` div, and the dialogs' hidden
          `ActPrintSheet`s render as siblings *outside* it — so printing from
          within an open dialog prints only the act sheet, not the whole
          page underneath it (the dialog chrome is already `print:hidden` by
          default, see `components/ui/dialog.tsx`, but that alone doesn't
          hide this page's own header/tabs/table). */}
      <div className="print:hidden">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold">Наявна Спецпродукція</h1>
          <p className="text-muted-foreground">
            Наявні друковані бланки суворої звітності та номери електронних
            протоколів
          </p>
        </div>
        <Card>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <Tabs onValueChange={changeStatusTab} value={tab}>
                <TabsList className="!h-10 justify-start">
                  {warehouseTabStatuses.map((status) => (
                    <TabsTrigger className="px-4 text-sm" key={status} value={status}>
                      {warehouseTabLabels[status]}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground" htmlFor="blanks-type-filter">
                  Показувати
                </label>
                <Select onValueChange={changeTypeFilter} value={typeFilter}>
                  <SelectTrigger className="w-56" id="blanks-type-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouseTypeFilters.map((f) => (
                      <SelectItem key={f} value={f}>
                        {warehouseTypeFilterLabels[f]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {tabState.error ? (
              <p className="text-sm text-destructive">{tabState.error}</p>
            ) : !tabState.isLoading && tabState.blanks.length === 0 ? (
              <p className="py-8 text-center text-muted-foreground">
                {typeFilter === "all"
                  ? "Бланків у цьому статусі немає"
                  : `Одиниць типу «${warehouseTypeFilterLabels[typeFilter]}» у цьому статусі немає`}
              </p>
            ) : (
              <DataTable
                columns={columns}
                data={tabState.blanks}
                defaultSorting={[{ id: "series", desc: false }]}
                enablePagination={false}
                enableRowSelection={
                  canSelectForDestruction ? (row) => eligibleIds.has(row.original.id) : undefined
                }
                fillAvailableHeight
                footer={
                  <>
                    {tabState.hasMore && (
                      <div className="flex justify-center border-t p-3">
                        <Button
                          disabled={tabState.isLoadingMore}
                          onClick={() => loadMore(tab, typeFilter)}
                          variant="outline"
                        >
                          {tabState.isLoadingMore ? "Завантаження…" : "Завантажити ще"}
                        </Button>
                      </div>
                    )}
                    {/* Inside `DataTable`'s own scroll container and `sticky
                        bottom-0` (mirroring the header's `sticky top-0`): below
                        a `fillAvailableHeight` table the bar would land under
                        the viewport's bottom edge, invisible right when a
                        checkbox is ticked. Sticky keeps it pinned to the
                        table's bottom edge while still occupying its own space
                        at the end of the list, so it never permanently covers
                        the last row. */}
                    {canSelectForDestruction && tabMode && selectedEligibleIds.length > 0 && (
                      <div className="sticky bottom-0 z-20 flex flex-wrap items-center justify-between gap-3 border-t bg-background p-3">
                        <span className="text-sm">Обрано бланків: {selectedEligibleIds.length}</span>
                        <div className="flex gap-2">
                          <Button onClick={() => setRowSelection({})} size="sm" variant="ghost">
                            Зняти вибір
                          </Button>
                          <Button
                            onClick={() => setActDraft({ mode: tabMode, ids: selectedEligibleIds })}
                            size="sm"
                          >
                            <Trash2 data-icon="inline-start" />
                            {tabMode === "annulled"
                              ? `Скласти акт знищення (${selectedEligibleIds.length})`
                              : `Знищити вибрані (${selectedEligibleIds.length})`}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                }
                getRowId={(b) => b.id}
                isLoading={tabState.isLoading}
                onRowSelectionChange={canSelectForDestruction ? setRowSelection : undefined}
                rowActions={(row) => renderRowActions(row.original)}
                rowSelection={canSelectForDestruction ? rowSelection : undefined}
                tableClassName={tab === "annulled" ? "table-auto" : "table-fixed"}
              />
            )}
          </CardContent>
        </Card>
      </div>

      <ActViewDialog
        actId={viewedActId}
        onActChanged={applyDestructionAct}
        onOpenChange={(open) => !open && setViewedActId(null)}
      />
      {actDraft && actDraftBlanks.length > 0 && (
        <DestructionActDialog
          blanks={actDraftBlanks}
          key={`${actDraft.mode}:${actDraft.ids.join(",")}`}
          mode={actDraft.mode}
          onFinalize={handleFinalize}
          onOpenChange={(open) => !open && setActDraft(null)}
          open
          subjectName={subjectName}
          userName={userName}
        />
      )}
    </>
  )
}
